import {DateRange} from '@/components/ui/scrollCalendar';

/**
 * Formats a date range for display in the mobile search component.
 *
 * @param dateRange - The selected date range or undefined if no dates selected
 * @returns Formatted string representation of the date(s):
 *   - "Dates" when no date is selected
 *   - "Jul 9" for single date selection
 *   - "Jul 9 - 25" for date range within same month
 *   - "Jul 9 - Aug 20" for date range spanning different months
 */
export const formatDateRange = (dateRange: DateRange | undefined): string => {
  if (!dateRange?.from) return 'Anytime';

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (!dateRange.to || dateRange.from.getTime() === dateRange.to.getTime()) {
    return formatDate(dateRange.from);
  }

  const fromFormatted = formatDate(dateRange.from);
  const toFormatted = formatDate(dateRange.to);

  // Check if same month
  if (
    dateRange.from.getMonth() === dateRange.to.getMonth() &&
    dateRange.from.getFullYear() === dateRange.to.getFullYear()
  ) {
    const fromDay = dateRange.from.getDate();
    return `${dateRange.from.toLocaleDateString('en-US', {month: 'short'})} ${fromDay} - ${dateRange.to.getDate()}`;
  }

  return `${fromFormatted} - ${toFormatted}`;
};

/**
 * Formats a date with context-aware display:
 * - Yesterday → "Yesterday, Mar 26"
 * - Today → "Today, Mar 26"
 * - Tomorrow → "Tomorrow, Mar 26"
 * - Current year → "Sat March 26" (weekday + full month, no year)
 * - Other year → "March 26, 2026" (full month + year, no weekday)
 *
 * @param date - A Date object, date string, or null
 * @returns Formatted date string, or "Date TBD" if no date provided
 */
export function formatDate(date: Date | string | null): string {
  if (!date) return 'Date TBD';

  const d = new Date(date);
  const now = new Date();

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round(
    (startOfDate.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24)
  );

  const shortDate = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  if (diffDays === -1) return `Yesterday, ${shortDate}`;
  if (diffDays === 0) return `Today, ${shortDate}`;
  if (diffDays === 1) return `Tomorrow, ${shortDate}`;

  const currentYear = now.getFullYear() === d.getFullYear();

  if (currentYear) {
    const weekday = d.toLocaleDateString('en-US', {weekday: 'short'});
    const fullDate = d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
    return `${weekday} ${fullDate}`;
  }

  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a date as short month + year, e.g. "Jan 2026". Used for tenure
 * copy like "Member since Jan 2026" where day-level precision adds noise.
 */
export function formatMonthYear(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Formats an event's date range for display. Shows a range when both
 * startDate and endDate are present and different, otherwise shows
 * a single date.
 *
 * Examples:
 *  - "Jan 2 - Dec 18, 2026" (same year)
 *  - "Dec 28, 2025 - Jan 5, 2026" (different years)
 *  - "Feb 25, 2026" (single date or no endDate)
 *  - "Date TBD" (no dates)
 */
export function formatEventDate(
  startDate: Date | string | null | undefined,
  endDate?: Date | string | null | undefined
): string {
  if (!startDate) return 'Date TBD';

  const start = new Date(startDate);

  if (!endDate) return formatDate(startDate);

  const end = new Date(endDate);

  // Same day — show single date
  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()
  ) {
    return formatDate(startDate);
  }

  const sameYear = start.getFullYear() === end.getFullYear();

  if (sameYear) {
    // "Jan 2 - Dec 18, 2026"
    const startStr = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const endStr = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startStr} - ${endStr}`;
  }

  // "Dec 28, 2025 - Jan 5, 2026"
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

/**
 * Formats a Date object with a time component into a 12 hour time.
 * (e.g. 12:30 PM)
 * @param date - A Date object, date string, or null
 * @returns Formatted time string, or null if no date provided
 */
export function formatTime(date: Date | string | null): string | undefined {
  if (!date) return;
  const d = new Date(date);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const METERS_PER_MILE = 1609.34;
const FEET_PER_METER = 3.28084;

/** Converts meters to miles. */
export function metersToMiles(meters: number): number {
  return meters / METERS_PER_MILE;
}

/** Converts meters to feet. */
export function metersToFeet(meters: number): number {
  return meters * FEET_PER_METER;
}

/**
 * Formats a distance in meters to a human-readable miles or km string.
 *
 * @param meters - Distance in meters
 * @returns Formatted string like "42 mi" or "0.8 km", or null if no value
 */
export function formatDistance(
  meters: number | null | undefined
): string | null {
  if (!meters) return null;
  const miles = metersToMiles(meters);

  if (miles >= 1) {
    return `${Math.round(miles).toLocaleString()} mi`;
  }

  const feet = metersToFeet(meters);
  return `${Math.round(feet).toLocaleString()} ft`;
}

/**
 * Formats an elevation gain in meters to a human-readable feet string.
 *
 * @param meters - Elevation gain in meters
 * @returns Formatted string like "3,200 ft", or null if no value
 */
export function formatElevation(
  meters: number | null | undefined
): string | null {
  if (!meters) return null;
  const feet = metersToFeet(meters);
  return `${Math.round(feet).toLocaleString()} ft`;
}

/**
 * Formats a Date as a local YYYY-MM-DD string (e.g. "2026-03-11").
 * Uses local date components to avoid UTC off-by-one issues that
 * occur with toISOString().
 */
export function formatLocalDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Returns a human-readable string for how far away a future date is.
 * e.g. "Tomorrow", "in 3 days", "in 2 weeks"
 */
export function daysUntil(date: Date | string): string {
  const target = new Date(date);
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfTarget = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate()
  );
  const diffDays = Math.round(
    (startOfTarget.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) return 'Past';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 14) return `in ${diffDays} days`;
  const weeks = Math.floor(diffDays / 7);
  if (weeks < 8) return `in ${weeks} weeks`;
  const months = Math.floor(diffDays / 30);
  return `in ${months} months`;
}

/** Formats a date as a relative time string (e.g. "2 days ago"). */
export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  return then.toLocaleDateString();
}

export function toTitleCase(str: string): string {
  // Normalize the string to lowercase first for consistency
  str = str.toLowerCase();
  // Use a regex to match the first letter of each word (\\b\\w) globally (g)
  return str.replace(/\b\w/g, (s) => s.toUpperCase());
}
